import React, { useEffect, useState } from "react";
import { Badge, Breadcrumb, BreadcrumbItem, Button, Card, Col, Container, Form, Modal, Nav, Row, Table } from "react-bootstrap";
import { HiOutlineRefresh } from "react-icons/hi";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { convertDateFormat } from "../../utils/helper";
import { GetDistance, GetEmployeeDailyAttList } from "../../utils/queries";

const getCurrentDate = () => {
  const today = new Date();
  const day = String(today.getDate()).padStart(2, "0");
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const year = today.getFullYear();
  return `${year}-${month}-${day}`;
};

const formatTimeFromApi = (apiDateString) => {
  const [datePart, timePart] = apiDateString.split(" ");
  const [day, month, year] = datePart.split("-").map(Number);
  const [hours, minutes] = timePart.split(":").map(Number);
  const date = new Date(year, month - 1, day, hours, minutes);

  let h = date.getHours();
  const m = date.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}.${m.toString().padStart(2, "0")} ${ampm}`;
};

let fixedLatAndLong = process.env.REACT_APP_FIXED_LAT_AND_LONG;
let gMapKey = process.env.REACT_APP_GOOGLE_MAP_API_KEY_FOR_MAP;

const AdminAttendanceReports = () => {
  const [dateStr, setDateStr] = useState(getCurrentDate());
  const [staffList, setStaffList] = useState([]);
  const [distances, setDistances] = useState({});
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const { userInfo } = useSelector((state) => state.auth);
  const ReqCompanyCode = userInfo && userInfo[13]?.InfoField;

  const fetchData = async () => {
    try {
      const response = await GetEmployeeDailyAttList(convertDateFormat(dateStr), "", ReqCompanyCode);
      const action = response[0]?.ActionType;
      if (action > 0) {
        const JSONData1 = JSON.parse(response[0]?.JSONData1);
        setStaffList(JSONData1);
      } else {
        toast.warn(response[0]?.ErrorMessage || "Failed to load data");
      }
    } catch (error) {
      console.error("Error fetching attendance:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [dateStr]);

  useEffect(() => {
    const fetchDistances = async () => {
      const destinations = staffList
        .filter((member) => member.AttLocation)
        .map((member) => ({
          AttID: member.AttID,
          location: member.AttLocation,
        }));

      if (destinations.length === 0) return;

      const response = await GetDistance(destinations);
      const distanceMap = {};
      response[0]?.InfoField2?.forEach((item) => {
        distanceMap[item.AttID] = {
          distance: item.distance || "N/A",
          duration: item.duration || "N/A",
        };
      });

      setDistances(distanceMap);
    };

    if (staffList.length > 0) {
      fetchDistances();
    }
  }, [staffList]);

  return (
    <>
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Route to {selectedMember?.EmployeeName} ({distances[selectedMember?.AttID]?.distance})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <iframe
            title={`Route to ${selectedMember?.EmployeeName}`}
            width="100%"
            height="450"
            loading="lazy"
            allowFullScreen
            style={{ border: 0 }}
            src={`https://www.google.com/maps/embed/v1/directions?key=${gMapKey}&origin=${fixedLatAndLong}&destination=${selectedMember?.AttLocation}&units=metric`}
          ></iframe>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Breadcrumb>
        <BreadcrumbItem>
          <Nav.Link as={Link} to="/dashboard" className="text-success text-decoration-none">
            Dashboard
          </Nav.Link>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <Nav.Link as={Link} to="/admin-attendance-management" className="text-success text-decoration-none">
            Attendance Management
          </Nav.Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Attendance Reports</BreadcrumbItem>
      </Breadcrumb>

      <Container className="py-4">
        <Row className="mb-3 align-items-center justify-content-between">
          <Col xs={12} md={6}>
            <h4 className="text-center">Admin Attendance Reports</h4>
          </Col>
          <Col xs={12} md="auto" className="d-flex gap-2 mt-2 mt-md-0">
            <Form.Control type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} />
            <Button variant="primary" onClick={fetchData}>
              <HiOutlineRefresh size={16} className="me-1" />
              Refresh
            </Button>
          </Col>
        </Row>

        {/* Show table on large screens */}
        <div className="d-none d-md-block">
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Staff ID</th>
                <th>Name</th>
                <th>Role</th>
                <th>Type</th>
                <th>Entry Time</th>
                <th>Image</th>
                <th>Distance</th>
              </tr>
            </thead>
            <tbody>
              {staffList.length > 0 ? (
                staffList.map((member) => {
                  const imgU = member.AttImage ? `${process.env.REACT_APP_API_URL}${member.AttImage}` : "";
                  return (
                    <tr key={member.AttID}>
                      <td>{member.EMPCODE}</td>
                      <td>{member.EmployeeName}</td>
                      <td>{member.EmployeeType}</td>
                      <td>{member.AttType}</td>
                      <td>{formatTimeFromApi(member.AttDateTime)}</td>
                      <td>{imgU && <img src={imgU} alt="staff" style={{ maxWidth: "100px" }} />}</td>
                      <td>
                        <button
                          type="button"
                          className="underline btn btn-primary btn-sm"
                          onClick={() => {
                            setShowModal(true);
                            setSelectedMember(member);
                          }}
                        >
                          {distances[member.AttID]
                            ? `${distances[member.AttID].distance} (${distances[member.AttID].duration})`
                            : "Calculating..."}
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={7}>No Data Found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Show card view on small screens */}
        <div className="d-md-none">
          <Row xs={1} md={2} lg={3} className="g-4">
            {staffList.length > 0 ? (
              staffList.map((member) => {
                const imgU = member.AttImage ? `${process.env.REACT_APP_API_URL}${member.AttImage}` : "";
                const distanceInfo = distances[member.AttID];
                const distanceText = distanceInfo ? `${distanceInfo.distance} (${distanceInfo.duration})` : "Calculating...";

                return (
                  <Col key={member.AttID}>
                    <Card className="shadow border-0 h-100">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <Card.Title className="mb-0">{member.EmployeeName}</Card.Title>
                            <Card.Subtitle className="text-muted">{member.EmployeeType}</Card.Subtitle>
                          </div>
                          <Badge bg="info" pill>
                            {member.AttType}
                          </Badge>
                        </div>

                        <div className="mb-2">
                          <strong>Staff ID:</strong> {member.EMPCODE}
                        </div>
                        <div className="mb-2">
                          <strong>Entry:</strong> {formatTimeFromApi(member.AttDateTime)}
                        </div>

                        {imgU && (
                          <div className="mb-3 text-center">
                            <img
                              src={imgU}
                              alt="staff"
                              style={{ width: "100px", height: "100px", objectFit: "cover", borderRadius: "50%" }}
                            />
                          </div>
                        )}

                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setSelectedMember(member);
                            setShowModal(true);
                          }}
                        >
                          {distanceText}
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })
            ) : (
              <p>No Data Found</p>
            )}
          </Row>
        </div>
      </Container>
    </>
  );
};

export default AdminAttendanceReports; 